import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Brain, FileText, Shield, CheckCircle, Magnet, Palette, Rocket, ExternalLink } from "lucide-react";
import heroQuizQualificaLeads from "@/assets/blog/hero-quiz-qualifica-leads.jpg";
import heroAtencaoSemAnuncios from "@/assets/blog/hero-atencao-sem-anuncios.jpg";
import heroDiagnosticoIA from "@/assets/blog/hero-diagnostico-ia-consultoria.jpg";
import heroFormulariosDinamicos from "@/assets/blog/hero-formularios-dinamicos.jpg";
import heroFormularioCapturaLeads from "@/assets/blog/hero-formulario-captura-leads.jpg";

const Index = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-border/40 bg-background/80 backdrop-blur-xl">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <img src="/brand/app-icon.svg" alt="" aria-hidden="true" className="h-8 w-8 rounded-lg" />
            <span className="text-lg font-semibold">Magnet Link</span>
          </div>
          <nav className="hidden items-center gap-6 md:flex" role="navigation" aria-label="Navegação principal">
            <a href="#features" className="text-sm text-muted-foreground transition-colors hover:text-foreground">
              Recursos
            </a>
            <a href="#how-it-works" className="text-sm text-muted-foreground transition-colors hover:text-foreground">
              Como Funciona
            </a>
            <Link to="/blog" className="text-sm text-muted-foreground transition-colors hover:text-foreground">
              Blog
            </Link>
          </nav>
          <Button asChild>
            <Link to="/auth">Entrar</Link>
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/4 left-1/4 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />
        </div>
        <div className="container mx-auto px-4 text-center">
          <div className="mx-auto max-w-3xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-muted/50 px-4 py-1.5 text-sm">
              <Magnet className="h-4 w-4 text-primary" />
              <span>Formulários inteligentes com IA</span>
            </div>
            <h1 className="mb-6 text-4xl font-bold tracking-tight md:text-6xl lg:text-7xl">
              Capture leads com{" "}
              <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                perguntas dinâmicas
              </span>
            </h1>
            <p className="mx-auto mb-10 max-w-2xl text-lg text-muted-foreground md:text-xl">
              Crie formulários que fazem perguntas personalizadas com IA, gerando PDFs 
              automáticos e coletando dados valiosos dos seus leads.
            </p>
            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Button size="lg" asChild className="gap-2 px-8">
                <Link to="/auth?mode=signup">
                  Criar Meu Formulário
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/f/demo">Ver Demonstração</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="border-t border-border/40 bg-muted/30 py-20 md:py-32">
        <div className="container mx-auto px-4">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">
              Recursos Poderosos
            </h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              Tudo que você precisa para criar formulários inteligentes que convertem
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <Card className="border-border/50 bg-card/50 backdrop-blur">
              <CardContent className="p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                  <Brain className="h-6 w-6 text-primary" />
                </div>
                <h3 className="mb-2 text-lg font-semibold">Perguntas com IA</h3>
                <p className="text-sm text-muted-foreground">
                  A IA gera perguntas personalizadas baseadas nas respostas anteriores do lead
                </p>
              </CardContent>
            </Card>
            <Card className="border-border/50 bg-card/50 backdrop-blur">
              <CardContent className="p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <h3 className="mb-2 text-lg font-semibold">PDF Automático</h3>
                <p className="text-sm text-muted-foreground">
                  Gere PDFs personalizados automaticamente ao final de cada formulário
                </p>
              </CardContent>
            </Card>
            <Card className="border-border/50 bg-card/50 backdrop-blur">
              <CardContent className="p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                  <Palette className="h-6 w-6 text-primary" />
                </div>
                <h3 className="mb-2 text-lg font-semibold">Personalização Total</h3>
                <p className="text-sm text-muted-foreground">
                  Customize cores, logo e campos do formulário para sua marca
                </p>
              </CardContent>
            </Card>
            <Card className="border-border/50 bg-card/50 backdrop-blur">
              <CardContent className="p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <h3 className="mb-2 text-lg font-semibold">Dados Seguros</h3>
                <p className="text-sm text-muted-foreground">
                  Todos os dados são criptografados e armazenados com segurança
                </p>
              </CardContent>
            </Card>
            <Card className="border-border/50 bg-card/50 backdrop-blur">
              <CardContent className="p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                  <Rocket className="h-6 w-6 text-primary" />
                </div>
                <h3 className="mb-2 text-lg font-semibold">Rápido e Simples</h3>
                <p className="text-sm text-muted-foreground">
                  Crie seu formulário em minutos e comece a capturar leads hoje
                </p>
              </CardContent>
            </Card>
            <Card className="border-border/50 bg-card/50 backdrop-blur">
              <CardContent className="p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                  <CheckCircle className="h-6 w-6 text-primary" />
                </div>
                <h3 className="mb-2 text-lg font-semibold">Dashboard Completo</h3>
                <p className="text-sm text-muted-foreground">
                  Acompanhe todas as respostas e gerencie seus formulários em um só lugar
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 md:py-32">
        <div className="container mx-auto px-4">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">
              Como Funciona
            </h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              Em apenas 3 passos simples, você terá seu formulário inteligente funcionando
            </p>
          </div>
          <div className="mx-auto grid max-w-4xl gap-8 md:grid-cols-3">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary text-2xl font-bold text-primary-foreground">
                1
              </div>
              <h3 className="mb-2 text-lg font-semibold">Crie o Formulário</h3>
              <p className="text-sm text-muted-foreground">
                Configure os campos iniciais e as instruções para a IA
              </p>
            </div>
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary text-2xl font-bold text-primary-foreground">
                2
              </div>
              <h3 className="mb-2 text-lg font-semibold">Compartilhe o Link</h3>
              <p className="text-sm text-muted-foreground">
                Envie o link único para seus leads responderem
              </p>
            </div>
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary text-2xl font-bold text-primary-foreground">
                3
              </div>
              <h3 className="mb-2 text-lg font-semibold">Colete Respostas</h3>
              <p className="text-sm text-muted-foreground">
                A IA faz perguntas personalizadas e gera o PDF automaticamente
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section id="blog" className="border-t border-border/40 py-20 md:py-32" aria-labelledby="blog-heading">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h2 id="blog-heading" className="mb-4 text-3xl font-bold md:text-4xl">
              Blog
            </h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              Estratégias, dicas e tutoriais para capturar mais leads e converter visitantes em clientes
            </p>
          </div>
          
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {/* Post 1 - Mais recente */}
            <Link
              to="/blog/como-criar-quiz-que-qualifica-leads-automaticamente"
              className="group block overflow-hidden rounded-xl border border-border/50 bg-card/50 backdrop-blur transition-all hover:border-primary/50 hover:shadow-lg"
            >
              <div className="aspect-video bg-gradient-to-br from-primary/20 to-accent/20 relative overflow-hidden">
                <img
                  src={heroQuizQualificaLeads}
                  alt="Ilustração 3D de ímã de ferradura branco atraindo cartões de quiz com alternativas marcadas e avatares entrando em um funil roxo"
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
                <span className="absolute bottom-4 left-4 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
                  Qualificação de Leads
                </span>
              </div>
              <div className="p-5">
                <h3 className="mb-2 text-lg font-semibold group-hover:text-primary transition-colors line-clamp-2">
                  Como Criar um Quiz que Qualifica Leads Automaticamente
                </h3>
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                  Perguntas que qualificam, lead scoring automático, IA adaptativa e diagnóstico personalizado em PDF.
                </p>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>16 min</span>
                  <span>•</span>
                  <span>18 Ago 2026</span>
                </div>
              </div>
            </Link>

            {/* Post 2 */}
            <Link
              to="/blog/como-conquistar-atencao-sem-gastar-mais-em-anuncios"
              className="group block overflow-hidden rounded-xl border border-border/50 bg-card/50 backdrop-blur transition-all hover:border-primary/50 hover:shadow-lg"
            >
              <div className="aspect-video bg-gradient-to-br from-primary/20 to-accent/20 relative overflow-hidden">
                <img
                  src={heroAtencaoSemAnuncios}
                  alt="Ilustração 3D de ímã de ferradura branco atraindo ícones de olhos, comentários, corações e megafone sobre fundo gradiente roxo"
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
                <span className="absolute bottom-4 left-4 rounded-full bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">
                  Tráfego & Atenção
                </span>
              </div>
              <div className="p-5">
                <h3 className="mb-2 text-lg font-semibold group-hover:text-primary transition-colors line-clamp-2">
                  Como Conquistar Atenção Sem Gastar Mais em Anúncios
                </h3>
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                  9 estratégias para gerar leads com conteúdo útil, IA e experiências interativas — sem aumentar a verba de mídia.
                </p>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>15 min</span>
                  <span>•</span>
                  <span>8 Ago 2026</span>
                </div>
              </div>
            </Link>


            {/* Posts 3-5 verticais */}
            <div className="space-y-3 flex flex-col justify-between h-full md:col-span-2 xl:col-span-1">
              <Link
                to="/blog/como-criar-formulario-de-captura-de-leads-que-converte"
                className="group flex gap-3 overflow-hidden rounded-lg border border-border/50 bg-card/50 p-3 transition-all hover:border-primary/50 flex-1"
              >
                <div className="w-16 h-16 flex-shrink-0 rounded-md overflow-hidden">
                  <img
                    src={heroFormularioCapturaLeads}
                    alt="Ilustração 3D de ímã de ferradura atraindo campos de formulário e envelope"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs text-primary font-medium">Captura de Leads</span>
                  <h4 className="font-semibold text-sm group-hover:text-primary transition-colors line-clamp-1">
                    Formulário de Captura que Converte
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                    Estrutura, copy, campos ideais e testes A/B
                  </p>
                  <span className="text-xs text-muted-foreground mt-1">21 Jul 2026</span>
                </div>
              </Link>

              <Link
                to="/blog/diagnostico-com-ia-para-consultores-capturar-leads"
                className="group flex gap-3 overflow-hidden rounded-lg border border-border/50 bg-card/50 p-3 transition-all hover:border-primary/50 flex-1"
              >
                <div className="w-16 h-16 flex-shrink-0 rounded-md overflow-hidden">
                  <img
                    src={heroDiagnosticoIA}
                    alt="Ilustração 3D de ímã branco atraindo relatório de diagnóstico com gráficos e cérebro"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs text-accent font-medium">Lead Magnets & IA</span>
                  <h4 className="font-semibold text-sm group-hover:text-primary transition-colors line-clamp-1">
                    Diagnóstico com IA: O Lead Magnet de 2026
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                    Converte até 3x mais que e-books
                  </p>
                  <span className="text-xs text-muted-foreground mt-1">1 Jun 2026</span>
                </div>
              </Link>

              <Link
                to="/blog/como-criar-formularios-dinamicos-que-convertem"
                className="group flex gap-3 overflow-hidden rounded-lg border border-border/50 bg-card/50 p-3 transition-all hover:border-primary/50 flex-1"
              >
                <div className="w-16 h-16 flex-shrink-0 rounded-md overflow-hidden">
                  <img
                    src={heroFormulariosDinamicos}
                    alt="Ilustração 3D de ímã branco atraindo campos de formulário flutuantes"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs text-primary font-medium">UX & Formulários</span>
                  <h4 className="font-semibold text-sm group-hover:text-primary transition-colors line-clamp-1">
                    Formulários Dinâmicos: Triplicar Conversões
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                    Lógica condicional, IA generativa e fluxo adaptativo
                  </p>
                  <span className="text-xs text-muted-foreground mt-1">15 Mai 2026</span>
                </div>
              </Link>

            </div>
          </div>

          
          <div className="mt-10 text-center">
            <Button variant="outline" asChild>
              <Link to="/blog">Ver Todos os Artigos</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="border-t border-border/40 bg-muted/30 py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="mb-4 text-3xl font-bold md:text-4xl">
            Pronto para Começar?
          </h2>
          <p className="mx-auto mb-8 max-w-xl text-muted-foreground">
            Crie seu primeiro formulário inteligente gratuitamente e veja como a IA pode 
            transformar a captura de leads do seu negócio.
          </p>
          <Button size="lg" asChild className="gap-2 px-8">
            <Link to="/auth">
              Criar Conta Grátis
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/40 py-8" role="contentinfo" aria-label="Rodapé do site">
        <div className="container mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} Magnet Link. Todos os direitos reservados.</p>
          <a
            href="https://shinier.com.br"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-sm"
            aria-label="Visitar site da Shinier, empresa aceleradora (abre em nova aba)"
          >
            Empresa Acelerada pela <span className="text-primary font-bold">Shinier</span>
            <ExternalLink className="w-4 h-4" aria-hidden="true" />
          </a>
        </div>
      </footer>
    </div>
  );
};

export default Index;
